import { computed, ref } from 'vue';
import { B2BClientService, ProductPriceModel, ProductDiscountType } from '@/b2b/shared/api/server-apis';
import utilities from '@/shared/utilities.service';
import authStore from '@/shared/auth/auth.store';
import { useModuleConfigurator } from '@/shared/product/details/ui/module-configurator/useModuleConfigurator';
import { VariantModulePriceModel, VariantModuleDiscountModel } from '@/types/productServerApi';

const netPrices = ref<{ [key: string]: ProductPriceModel; }[] | {}>({});
const notInAssortment = ref<string[]>([]);

export default function useNetPrices() {
    const { moduleConfiguratorTileData } = useModuleConfigurator();

    const isSignedInAsPartner = computed<boolean>(() => {
        return authStore.isSignedInAsPartner;
    });

    const fetchNetPrices = async(variantIdList: string[], cachebust: boolean = false) => {
        if (!isSignedInAsPartner.value) return;
        const itemsWithNetPrice = Object.keys(netPrices.value);
        const itemsWithoutNetPrice = cachebust ? variantIdList : variantIdList.filter((itemId) => itemsWithNetPrice.indexOf(itemId) === -1);

        if (itemsWithoutNetPrice.length > 0 && !moduleConfiguratorTileData.value?.modules) {
            const response = await B2BClientService.getPartnerProductPrices(itemsWithoutNetPrice).then(utilities.unwrapModel);
            netPrices.value = Object.assign(netPrices.value, response);
            handleNotInAssortment(itemsWithoutNetPrice);
        }

        if (itemsWithoutNetPrice.length > 0 && (moduleConfiguratorTileData.value && moduleConfiguratorTileData.value.modules && moduleConfiguratorTileData.value.modules.length > 0)) {
            const firstSku = moduleConfiguratorTileData.value?.modules[0].sku;
            const response = await B2BClientService.getPartnerModulePrice(moduleConfiguratorTileData.value?.modules).then(utilities.unwrapModel);
            netPrices.value = { [firstSku]: response };
            handleNotInAssortment(itemsWithoutNetPrice);
        }
    };

    const getNetPrice = (variantId: string): ProductPriceModel | undefined => {
        if (netPrices.value && netPrices.value[variantId]) {
            return netPrices.value[variantId];
        }
        return undefined;
    };

    const getNetPrices = async(variantIds: string[]): Promise<{ [key: string]: VariantModulePriceModel; }> => {
        await fetchNetPrices(variantIds);
        const mappedNetPrices: { [key: string]: VariantModulePriceModel; } = {};

        variantIds.forEach(variantId => {
            const mappedNetPrice = mapVariantNetPrice(variantId);

            if (mappedNetPrice) {
                mappedNetPrices[variantId] = mappedNetPrice;
            }
        });

        return mappedNetPrices;
    };

    const mapVariantNetPrice = (variantId: string): VariantModulePriceModel | undefined => {
        const variantNetPrice = getNetPrice(variantId);
        if (!variantNetPrice) return undefined;

        const { listPrice, discount, salesPrice } = variantNetPrice;
        let netPriceDiscount: VariantModuleDiscountModel | undefined;

        if (discount !== undefined) {
            netPriceDiscount = {
                percentage: discount.percentage,
                type: discount.type === ProductDiscountType.MyBoliaBoost
                    ? ProductDiscountType.MyBoliaBoost.toString()
                    : ProductDiscountType.Campaign.toString(),
                expiresDate: undefined,
                discount: { amount: discount.discount.amount, currency: discount.discount.currency, rawAmount: discount.discount.rawAmount },
            };
        }

        return {
            listPrice: { ...listPrice },
            discount: netPriceDiscount,
            ecoTax: undefined,
            salesPrice: { ...salesPrice },
        };
    };

    const getIsNotInAssortment = (variantId: string): boolean => {
        const found = notInAssortment.value.find((item) => item === variantId);
        return !!found;
    };

    const handleNotInAssortment = (skuList: string[]) => {
        const notInAssortmentList = skuList.filter((sku) => !getNetPrice(sku));
        notInAssortment.value = [...notInAssortment.value, ...notInAssortmentList];
    };

    return {
        netPrices,
        fetchNetPrices,
        getNetPrice,
        getNetPrices,
        getIsNotInAssortment,
    };
}
